import { buildChatWebSocketUrl } from '@/lib/realtime/ws-url';

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

/**
 * WebSocket client with automatic reconnect and exponential backoff (matches product need for resilient chat).
 */
export class ReconnectingChatWebSocket {
  constructor({ getToken, onStatus, onOpen, onJsonMessage }) {
    this.getToken = getToken;
    this.onStatus = onStatus;
    this.onOpen = onOpen;
    this.onJsonMessage = onJsonMessage;
    this.manualClose = false;
    this.reconnectTimer = null;
    this.socket = null;
    this.backoffMs = INITIAL_BACKOFF_MS;
  }

  connect() {
    this.manualClose = false;
    this._connectSocket(false);
  }

  disconnect() {
    this.manualClose = true;
    this._clearReconnectTimer();
    if (this.socket) {
      const toClose = this.socket;
      this.socket = null;
      toClose.close();
    }
    this.onStatus?.('disconnected');
  }

  send(payload) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }
    this.socket.send(JSON.stringify(payload));
  }

  _clearReconnectTimer() {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  _connectSocket(isReconnect) {
    this._clearReconnectTimer();

    const token = String(this.getToken?.() || '').trim();
    if (!token) {
      this.onStatus?.('disconnected');
      return;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.onStatus?.(isReconnect ? 'reconnecting' : 'connecting');

    let url;
    try {
      url = buildChatWebSocketUrl(token);
    } catch (_error) {
      this.onStatus?.('error');
      this._scheduleReconnect();
      return;
    }

    const browserSocket = new WebSocket(url);
    this.socket = browserSocket;

    browserSocket.addEventListener('open', () => {
      this.backoffMs = INITIAL_BACKOFF_MS;
      this.onStatus?.('connected');
      this.onOpen?.();
    });

    browserSocket.addEventListener('close', () => {
      const wasCurrent = this.socket === browserSocket;
      if (this.socket === browserSocket) {
        this.socket = null;
      }
      if (!wasCurrent || this.manualClose) {
        if (this.manualClose) {
          this.onStatus?.('disconnected');
        }
        return;
      }

      const tokenAfter = String(this.getToken?.() || '').trim();
      if (!tokenAfter) {
        this.onStatus?.('disconnected');
        return;
      }

      this.onStatus?.('reconnecting');
      this._scheduleReconnect();
    });

    browserSocket.addEventListener('error', () => {
      this.onStatus?.('error');
    });

    browserSocket.addEventListener('message', (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch (_error) {
        return;
      }
      this.onJsonMessage?.(payload);
    });
  }

  _scheduleReconnect() {
    this._clearReconnectTimer();
    if (this.manualClose) {
      return;
    }
    const token = String(this.getToken?.() || '').trim();
    if (!token) {
      this.onStatus?.('disconnected');
      return;
    }

    const delay = Math.min(this.backoffMs, MAX_BACKOFF_MS);
    this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS);

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.manualClose) {
        return;
      }
      if (!String(this.getToken?.() || '').trim()) {
        this.onStatus?.('disconnected');
        return;
      }
      this._connectSocket(true);
    }, delay);
  }
}
